import { apiSlice } from "../../app/apiSlice";
import type { RootState } from "../../app/store";
import { API_ENDPOINTS } from "../../constants/apiEndpoints";
import { updateUser } from "./auth.slice";
import type {
	CredentialsLoginRequest,
	LoginResponse,
	SamlExchangeRequest,
} from "./auth.types";
import { mergeSessionUser, parseLoginResponse } from "./auth.utils";

export const authApi = apiSlice.injectEndpoints({
	endpoints: (builder) => ({
		login: builder.mutation<LoginResponse, CredentialsLoginRequest>({
			query: (body) => ({
				url: API_ENDPOINTS.auth.login,
				method: "POST",
				body,
			}),
			transformResponse: parseLoginResponse,
			invalidatesTags: ["Auth"],
		}),
		exchangeSamlCode: builder.mutation<LoginResponse, SamlExchangeRequest>({
			query: (body) => ({
				url: API_ENDPOINTS.auth.samlExchange,
				method: "POST",
				body,
			}),
			transformResponse: parseLoginResponse,
			invalidatesTags: ["Auth"],
		}),
		logout: builder.mutation<void, void>({
			query: () => ({
				url: API_ENDPOINTS.auth.logout,
				method: "POST",
			}),
			invalidatesTags: ["Auth"],
		}),
		refreshToken: builder.mutation<
			{ accessToken: string; refreshToken: string },
			{ refreshToken: string }
		>({
			query: (body) => ({
				url: API_ENDPOINTS.auth.refresh,
				method: "POST",
				body,
			}),
		}),
		getCurrentUser: builder.query<LoginResponse["user"], void>({
			query: () => API_ENDPOINTS.auth.me,
			transformResponse: (raw) => parseLoginResponse(raw).user,
			providesTags: ["Auth"],
			async onQueryStarted(_, { dispatch, getState, queryFulfilled }) {
				try {
					const { data } = await queryFulfilled;
					if (!data?.id) return;
					const current = (getState() as RootState).auth.user;
					dispatch(updateUser(mergeSessionUser(current, data)));
				} catch {
					// Keep the persisted session; 401 handling in apiSlice force-logs out.
				}
			},
		}),
	}),
});

export const {
	useLoginMutation,
	useExchangeSamlCodeMutation,
	useLogoutMutation,
	useGetCurrentUserQuery,
	useRefreshTokenMutation,
} = authApi;
